// Shift score: the SCORING table (src/config/constants.js) applied to a sim's
// counters. Pure, no DOM -- the header chip, the end-of-shift card and the
// saved best score all read from here.

import { SCORING, PACES } from '../config/constants.js';

/**
 * What each scoring line counts, in display order. `free` is how the rules
 * line describes a line whose SCORING value is 0.
 */
export const SCORE_LINES = [
  { key: 'landing', label: 'Landings', rule: 'per landing', count: (s) => s.cleared },
  { key: 'nearMiss', label: 'Near misses prevented', rule: 'per near miss prevented', count: (s) => s.nearMisses },
  {
    key: 'adjustment',
    label: 'Adjustments',
    rule: 'per Delay, Reroute or Hold',
    free: 'Delay, Reroute and Hold are free',
    count: (s) => s.adjusted + s.holds,
  },
  { key: 'fieldScouted', label: 'Fields scouted', rule: 'per field scouted', count: (s) => s.ops?.scouted ?? 0, ops: true },
  { key: 'fieldSprayed', label: 'Fields sprayed', rule: 'per field sprayed', count: (s) => s.ops?.sprayed ?? 0, ops: true },
  {
    key: 'hazardHandled', label: 'Hazards handled', rule: 'per hazard handled', count: (s) => s.hazardsHandled ?? 0, ops: true,
  },
  {
    key: 'separationLoss', label: 'Separation lost', rule: 'per loss of separation',
    count: (s) => s.separationLosses ?? 0,
  },
  { key: 'birdStrike', label: 'Bird strikes', rule: 'per bird strike', count: (s) => s.birdStrikes ?? 0, ops: true },
  { key: 'incident', label: 'Incidents', rule: 'per incident', count: (s) => s.incidents },
];

/** Signed points as shown in the UI: "+10", "−2" (true minus sign), "0". */
export function formatPoints(n) {
  if (n > 0) return `+${n}`;
  if (n < 0) return `−${-n}`;
  return '0';
}

/** A score total as shown in the UI: "312", "−36". */
export function formatScore(n) {
  return n < 0 ? `\u2212${-n}` : String(n);
}

/** The points multiplier for a speed of play (1 for an unknown one). */
export function paceBonus(pace) {
  return PACES.find((p) => p.value === pace)?.bonus ?? 1;
}

/**
 * Record `gameSeconds` of play at speed `pace` (#13). The shift's speed
 * bonus is the bonus averaged over game time, so switching to a faster
 * speed just before the end earns only a little of it.
 */
export function trackPace(s, gameSeconds, pace) {
  s.paceTime = (s.paceTime ?? 0) + gameSeconds;
  s.paceWeighted = (s.paceWeighted ?? 0) + gameSeconds * paceBonus(pace);
}

/** The shift's speed multiplier so far (1 before any play). */
export function speedMultiplier(s) {
  return s.paceTime ? s.paceWeighted / s.paceTime : 1;
}

/**
 * Per-line breakdown and total for the current shift. A speed bonus line
 * (positive for faster play, negative for Slow) scales the points earned;
 * penalties aren't scaled.
 */
export function scoreBreakdown(s) {
  const lines = SCORE_LINES.filter((l) => !l.ops || s.ops).map(({ key, label, count }) => {
    const n = count(s);
    return { key, label, count: n, each: SCORING[key], points: n * SCORING[key] };
  });
  const earned = lines.reduce((sum, l) => sum + Math.max(0, l.points), 0);
  const mult = Math.round(speedMultiplier(s) * 100) / 100;
  const bonus = Math.round(earned * (mult - 1));
  if (bonus !== 0) {
    lines.push({
      key: 'speedBonus', label: 'Speed bonus', count: mult, each: null, points: bonus,
      note: `${earned} × ${mult.toFixed(2)}`,
    });
  }
  return { lines, total: lines.reduce((sum, l) => sum + l.points, 0) };
}

export function scoreOf(s) {
  return scoreBreakdown(s).total;
}

/** One-line explanation of the rules, e.g. "+10 per landing · +15 per near miss prevented · …". */
export function scoreRulesText({ ops = true } = {}) {
  return SCORE_LINES
    .filter((l) => ops || !l.ops)
    .filter(({ key, free }) => SCORING[key] !== 0 || free)
    .map(({ key, rule, free }) => (SCORING[key] === 0 ? free : `${formatPoints(SCORING[key])} ${rule}`))
    .concat(`faster play earns up to ×${Math.max(...PACES.map((p) => p.bonus))} points`)
    .join(' · ');
}
